import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import ProjectDetail from "@/pages/ProjectDetail";
import LaboratorioRedirect from "@/pages/LaboratorioRedirect";
import {
  ArrowUpRight,
  ArrowRight,
  Check,
  Plus,
  Minus,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
  Instagram,
  Linkedin,
  Sparkles,
  Hammer,
  Truck,
  Ruler,
  Leaf,
  ShieldCheck,
  Wrench,
  Clock,
  Factory,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const IS_DEV = process.env.NODE_ENV !== "production";

/* ---------- Motion presets (extracted to avoid inline object re-renders) ---------- */
const MOTION_MOBILE_NAV = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
};
const MOTION_FAQ_PANEL = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.3 },
};
const MOTION_FADE_UP_24 = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 } };
const MOTION_FADE_UP_30 = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 } };
const MOTION_FADE_UP_20 = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 } };
const VIEWPORT_ONCE = { once: true };
const VIEWPORT_ONCE_60 = { once: true, margin: "-60px" };
const VIEWPORT_ONCE_80 = { once: true, margin: "-80px" };
const staggerTransition = (i, duration = 0.6, step = 0.08) => ({ duration, delay: i * step });

/* ---------- Image library (real bathrooms / textures) ---------- */
const HERO_IMG =
  "https://images.unsplash.com/photo-1758548157466-7c454382035a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBiYXRocm9vbXxlbnwwfHx8fDE3Nzg2NTU1NjV8MA&ixlib=rb-4.1.0&q=85";

const GALLERY = [
  {
    slug: "suite-marble-hotel-boutique",
    src: "https://images.unsplash.com/photo-1638799869566-b17fa794c4de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBiYXRocm9vbXxlbnwwfHx8fDE3Nzg2NTU1NjV8MA&ixlib=rb-4.1.0&q=85",
    title: "Suite Marble · Hotel Boutique",
    place: "Madrid",
    year: "2024",
  },
  {
    slug: "industrial-loft-bath",
    src: "https://images.unsplash.com/photo-1733425992462-0f674e744ead?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwaW5kdXN0cmlhbCUyMGJhdGhyb29tfGVufDB8fHx8MTc3ODY1NTU1Mnww&ixlib=rb-4.1.0&q=85",
    title: "Industrial Loft Bath",
    place: "Barcelona",
    year: "2024",
  },
  {
    slug: "co-living-compact",
    src: "https://images.unsplash.com/photo-1635247049694-0269c357ab69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwaW5kdXN0cmlhbCUyMGJhdGhyb29tfGVufDB8fHx8MTc3ODY1NTU1Mnww&ixlib=rb-4.1.0&q=85",
    title: "Co-living Compact",
    place: "Valencia",
    year: "2023",
  },
  {
    slug: "residencial-premium",
    src: "https://images.unsplash.com/photo-1636063522013-8d8fbfe8fcdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxwcmVmYWIlMjBiYXRocm9vbSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Nzg2NTU1NTJ8MA&ixlib=rb-4.1.0&q=85",
    title: "Residencial Premium",
    place: "Bilbao",
    year: "2025",
  },
];

const MATERIALS = [
  {
    id: "concrete",
    label: "Hormigón pulido",
    sub: "Microcemento gris natural",
    img: "https://images.unsplash.com/photo-1632324875014-b5713099bef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxjb25jcmV0ZSUyMHRleHR1cmUlMjB3YWxsfGVufDB8fHx8MTc3ODY1NTU2NXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "wood",
    label: "Roble cepillado",
    sub: "Madera FSC tratada",
    img: "https://images.unsplash.com/photo-1760237655540-8197ef24838b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHx3b29kJTIwdGV4dHVyZSUyMGludGVyaW9yfGVufDB8fHx8MTc3ODY1NTU2NXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "marble",
    label: "Mármol Carrara",
    sub: "Veta clásica blanca",
    img: "https://images.unsplash.com/photo-1587749158407-58ef2b89ccf8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxtYXJibGUlMjB0ZXh0dXJlJTIwc2xhYnxlbnwwfHx8fDE3Nzg2NTU1NjV8MA&ixlib=rb-4.1.0&q=85",
  },
];

/* AI pre-generated bathroom images.
   3 sizes × 3 layouts × 3 materials × 2 variants (base / toallero) = 54 PNGs
   served as static files from /configurator/{size}-{layout}-{material}-{variant}.png */
const getPreviewSrc = (size, layout, material, hasToallero) => {
  const variant = hasToallero ? "toallero" : "base";
  return `/configurator/${size}-${layout}-${material}-${variant}.png`;
};

/* Fallback (in case AI image missing → use these real photos by material) */
const PREVIEW_FALLBACK = {
  concrete: GALLERY[1].src,
  wood: GALLERY[3].src,
  marble: GALLERY[0].src,
};

/* ---------- Reusable bits ---------- */
const Eyebrow = ({ children, num }) => (
  <div className="flex items-center gap-3">
    {num && <span className="num-eyebrow text-[#0A0A0A]">{num}</span>}
    <span className="overline">{children}</span>
  </div>
);

const PrimaryButton = ({ children, href, onClick, dark = true, testId, type = "button" }) => {
  const base =
    "group inline-flex items-center justify-center gap-3 px-7 py-4 text-[13px] font-medium tracking-wider uppercase transition-all duration-300";
  const dark_cls = "bg-[#0A0A0A] text-[#F7F7F7] hover:bg-[#333]";
  const light_cls = "bg-[#F7F7F7] text-[#0A0A0A] hover:bg-white";
  const cls = `${base} ${dark ? dark_cls : light_cls}`;
  if (href)
    return (
      <a href={href} className={cls} data-testid={testId}>
        {children}
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
      </a>
    );
  return (
    <button onClick={onClick} type={type} className={cls} data-testid={testId}>
      {children}
      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
    </button>
  );
};

const GhostButton = ({ children, href, onClick, testId }) => {
  const cls =
    "group inline-flex items-center gap-3 px-6 py-3 text-[13px] font-medium tracking-wider uppercase border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F7F7F7] transition-colors duration-300";
  if (href)
    return (
      <a href={href} className={cls} data-testid={testId}>
        {children}
        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </a>
    );
  return (
    <button onClick={onClick} type="button" className={cls} data-testid={testId}>
      {children}
      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
    </button>
  );
};

/* ---------- Header ---------- */
const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const links = [
    { label: "Concepto", href: "#concepto" },
    { label: "Beneficios", href: "#beneficios" },
    { label: "Configurador", href: "#configurador" },
    { label: "Proyectos", href: "#proyectos" },
    { label: "Proceso", href: "#proceso" },
    { label: "Contacto", href: "#contacto" },
  ];
  const linkHref = (h) => (isHome ? h : `/${h}`);
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F7F7F7]/75 border-b border-black/10"
    >
      <div className="mx-auto max-w-[1480px] px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2" data-testid="brand-logo">
          <span className="font-display text-[22px] tracking-tighter">MODULACCIÓN</span>
          <span className="overline -translate-y-[2px]">Baños</span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 lg:gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={linkHref(l.href)}
              className="text-[13px] tracking-wide font-medium link-underline whitespace-nowrap"
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:block">
          <PrimaryButton href={linkHref("#contacto")} testId="header-cta">Presupuesto</PrimaryButton>
        </div>
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center border border-[#0A0A0A]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menú"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            {...MOTION_MOBILE_NAV}
            className="md:hidden overflow-hidden bg-[#F7F7F7] border-t border-black/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={linkHref(l.href)}
                  onClick={() => setOpen(false)}
                  className="text-base tracking-wide"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <PrimaryButton href={linkHref("#contacto")} testId="mobile-header-cta">Solicitar presupuesto</PrimaryButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

/* ---------- Hero ---------- */
const Hero = () => (
  <section id="top" className="relative pt-[72px] bg-[#F7F7F7]" data-testid="hero">
    <div className="relative mx-auto max-w-[1480px] px-6 lg:px-10 pt-16 pb-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <Eyebrow num="01 — Baños prefabricados">Modulacción · ES · 2026</Eyebrow>
          <h1 className="font-display uppercase mt-6 text-[clamp(2.5rem,8vw,7.5rem)] text-[#0A0A0A]">
            El baño <span className="italic font-light">no se construye</span>.
            <br />
            <span className="block">Se instala.</span>
          </h1>
          <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-[#3a3a3a] font-light">
            Diseñamos, fabricamos y entregamos módulos de baño completos,
            listos para conectar. Precisión industrial, calidad de obra,
            instalación en horas — no en semanas.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <PrimaryButton href="#configurador" testId="hero-configurar-btn">Configura el tuyo</PrimaryButton>
            <GhostButton href="#proyectos" testId="hero-proyectos-btn">Ver proyectos</GhostButton>
          </div>
        </div>
        <div className="lg:col-span-5 grid grid-cols-3 gap-6 lg:pb-2">
          {[
            { k: "−72%", v: "tiempo de obra" },
            { k: "+1.200", v: "módulos entregados" },
            { k: "ISO 9001", v: "calidad certificada" },
          ].map((s) => (
            <div key={s.v} className="border-t border-black pt-4">
              <div className="font-display text-3xl">{s.k}</div>
              <div className="text-[11px] uppercase tracking-widest text-[#555] mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* image strip */}
      <div className="mt-16 relative overflow-hidden border border-black/10">
        <img
          src={HERO_IMG}
          alt="Baño moderno modular Modulacción"
          className="w-full h-[60vh] min-h-[460px] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        <div className="absolute left-6 bottom-6 lg:left-10 lg:bottom-10 text-white max-w-md">
          <Eyebrow num="MOD-A4">
            <span className="text-white/80">Módulo Suite 4.2m²</span>
          </Eyebrow>
          <p className="font-heading text-2xl mt-3 leading-tight">
            Premontado, transportado e instalado en menos de 6 horas.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Ribbon ---------- */
const Ribbon = () => {
  const items = [
    "Instalación en 24h",
    "Calidad premium",
    "Sostenibilidad",
    "Diseño a medida",
    "Fabricación CE",
    "Acabados arquitectónicos",
    "Cero residuos en obra",
    "Garantía 10 años",
  ];
  return (
    <div className="bg-[#0A0A0A] text-[#F7F7F7] py-6 border-y border-black" data-testid="benefits-ribbon">
      <Marquee speed={40} gradient={false} pauseOnHover={false}>
        {items.map((t) => (
          <span key={t} className="font-display text-3xl lg:text-4xl uppercase mx-8 flex items-center">
            {t}
            <span className="marquee-dot" />
          </span>
        ))}
      </Marquee>
    </div>
  );
};

/* ---------- Concepto (¿Qué es Modulacción?) Bento ---------- */
const Concepto = () => (
  <section id="concepto" className="py-28 lg:py-40 bg-[#F7F7F7]" data-testid="section-concepto">
    <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-5">
          <Eyebrow num="02">¿Qué es Modulacción?</Eyebrow>
          <h2 className="font-display text-5xl lg:text-6xl mt-6 uppercase">
            Industria + <br />
            arquitectura.
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <p className="text-lg leading-relaxed text-[#333]">
            Modulacción Baños es un sistema de fabricación industrial de baños
            completos. Cada módulo se diseña a medida, se construye en planta
            con tolerancias milimétricas y llega a obra listo para conectar a
            las acometidas. <span className="text-[#0A0A0A] font-medium">Sin polvo, sin sorpresas, sin retrasos.</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Big image left */}
        <div className="md:col-span-7 relative aspect-[5/4] overflow-hidden border border-black/10 group">
          <img
            src={GALLERY[0].src}
            alt="Modulacción concepto"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
          <div className="absolute left-6 bottom-6 text-white">
            <Eyebrow num="01">Concepto</Eyebrow>
          </div>
        </div>

        {/* stacked right */}
        <div className="md:col-span-5 flex flex-col gap-6">
          <div className="bg-white border border-black/10 p-8 flex-1">
            <Factory className="w-8 h-8" strokeWidth={1.2} />
            <h3 className="font-heading text-2xl mt-6">Fabricación industrial</h3>
            <p className="text-[15px] text-[#555] mt-3 leading-relaxed">
              Planta propia con control numérico, ensayos hidráulicos y
              pruebas de carga antes del envío.
            </p>
          </div>
          <div className="bg-[#0A0A0A] text-[#F7F7F7] p-8 flex-1">
            <Ruler className="w-8 h-8" strokeWidth={1.2} />
            <h3 className="font-heading text-2xl mt-6">Diseño a medida</h3>
            <p className="text-[15px] text-white/70 mt-3 leading-relaxed">
              Cada módulo se proyecta con el estudio o promotor. Sin
              catálogos rígidos: tu plano, nuestro módulo.
            </p>
          </div>
        </div>

        {/* bottom row */}
        <div className="md:col-span-4 bg-white border border-black/10 p-8">
          <Truck className="w-8 h-8" strokeWidth={1.2} />
          <h3 className="font-heading text-2xl mt-6">Logística</h3>
          <p className="text-[15px] text-[#555] mt-3 leading-relaxed">
            Transporte planificado y entrega justo a tiempo en obra.
          </p>
        </div>
        <div className="md:col-span-4 bg-white border border-black/10 p-8">
          <Wrench className="w-8 h-8" strokeWidth={1.2} />
          <h3 className="font-heading text-2xl mt-6">Instalación rápida</h3>
          <p className="text-[15px] text-[#555] mt-3 leading-relaxed">
            Equipos certificados conectan acometidas en horas, no semanas.
          </p>
        </div>
        <div className="md:col-span-4 bg-white border border-black/10 p-8">
          <Leaf className="w-8 h-8" strokeWidth={1.2} />
          <h3 className="font-heading text-2xl mt-6">Sostenibilidad</h3>
          <p className="text-[15px] text-[#555] mt-3 leading-relaxed">
            Cero residuos en obra. Materiales certificados FSC y reciclados.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Beneficios — split layout ---------- */
const Beneficios = () => {
  const items = [
    {
      icon: Clock,
      title: "Plazo previsible",
      body: "Reducimos hasta un 72% el tiempo de ejecución de los baños frente a la obra tradicional.",
    },
    {
      icon: ShieldCheck,
      title: "Calidad de planta",
      body: "Tolerancias milimétricas, controles unitarios y trazabilidad de cada componente.",
    },
    {
      icon: Hammer,
      title: "Menos coordinación",
      body: "Un único interlocutor sustituye a 7 oficios distintos en obra.",
    },
    {
      icon: Sparkles,
      title: "Acabados premium",
      body: "Mármol, microcemento, madera técnica y porcelánico XL — sin perder modulación.",
    },
  ];
  return (
    <section id="beneficios" className="bg-[#0A0A0A] text-[#F7F7F7] py-28 lg:py-40" data-testid="section-beneficios">
      <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <Eyebrow num="03"><span className="text-white/60">Beneficios</span></Eyebrow>
            <h2 className="font-display text-5xl lg:text-6xl uppercase mt-6">
              Más obra. <br /> Menos obra.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-sm">
              Hoteles, residencial colectivo, residencias de estudiantes o
              co-living. Donde haya un baño, hay un módulo posible.
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
              {items.map(({ icon: Icon, title, body }, i) => (
                <motion.div
                  key={title}
                  {...MOTION_FADE_UP_24}
                  transition={staggerTransition(i)}
                  viewport={VIEWPORT_ONCE_80}
                  className="bg-[#0A0A0A] p-10"
                >
                  <Icon className="w-9 h-9" strokeWidth={1.1} />
                  <h3 className="font-heading text-2xl mt-8">{title}</h3>
                  <p className="text-white/65 mt-4 leading-relaxed">{body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Configurador ---------- */
const SIZES = [
  { id: "S", label: "Compacto · 3.2 m²" },
  { id: "M", label: "Estándar · 4.5 m²" },
  { id: "L", label: "Suite · 6.8 m²" },
];

const LAYOUTS = [
  { id: "linear", label: "Lineal" },
  { id: "L", label: "En L" },
  { id: "open", label: "Open shower" },
];

const ACCESSORIES = [
  { key: "Espejo retroiluminado", restrictedSizes: [] },
  { key: "Toallero radiador", restrictedSizes: [] },
  { key: "Sanitario suspendido", restrictedSizes: [] },
  { key: "Ducha empotrada", restrictedSizes: [] },
  { key: "Lavabo doble", restrictedSizes: ["S", "M"], reason: "Solo disponible en tamaño L" },
  { key: "Banco de teca", restrictedSizes: [] },
];

const BASE_PRICE_BY_SIZE = { S: 7800, M: 11400, L: 16800 };
const MATERIAL_MULTIPLIER = { concrete: 1.0, wood: 1.1, marble: 1.35 };
const ACC_UNIT_PRICE = 320;

const computeEstimatedPrice = (size, material, accessoriesCount) =>
  Math.round(BASE_PRICE_BY_SIZE[size] * MATERIAL_MULTIPLIER[material] + accessoriesCount * ACC_UNIT_PRICE);

const buildConfigSummary = (size, layout, material, accessories, estimatedPrice) => ({
  config_size: SIZES.find((s) => s.id === size)?.label,
  config_layout: LAYOUTS.find((l) => l.id === layout)?.label,
  config_material: MATERIALS.find((m) => m.id === material)?.label,
  config_accessories: accessories,
  estimated_price: estimatedPrice,
});

const Configurador = () => {
  const [size, setSize] = useState("M");
  const [layout, setLayout] = useState("linear");
  const [material, setMaterial] = useState("marble");
  const [accessories, setAccessories] = useState(["Espejo retroiluminado", "Ducha empotrada"]);
  const [saved, setSaved] = useState(false);

  const toggleAcc = (a) => {
    const meta = ACCESSORIES.find((x) => x.key === a);
    if (meta?.restrictedSizes?.includes(size)) return; // ignored if not allowed
    setAccessories((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]
    );
  };

  // When size changes, drop accessories that are now restricted
  useEffect(() => {
    setAccessories((prev) =>
      prev.filter((a) => {
        const meta = ACCESSORIES.find((x) => x.key === a);
        return !meta?.restrictedSizes?.includes(size);
      })
    );
    // ACCESSORIES is a module-level constant; setAccessories is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  const hasToallero = accessories.includes("Toallero radiador");

  const estimatedPrice = useMemo(
    () => computeEstimatedPrice(size, material, accessories.length),
    [size, material, accessories.length]
  );

  const sendToContact = () => {
    const summary = buildConfigSummary(size, layout, material, accessories, estimatedPrice);
    localStorage.setItem("modulaccion_config", JSON.stringify(summary));
    window.dispatchEvent(new CustomEvent("modulaccion:config-saved", { detail: summary }));
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
    document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
  };

  const targetSrc = getPreviewSrc(size, layout, material, hasToallero);
  const fallbackSrc = PREVIEW_FALLBACK[material];

  // Debounced preview swap (1.5s after last interaction), with loading overlay
  // + preload so the swap is instant when the timer fires.
  const [displayedSrc, setDisplayedSrc] = useState(targetSrc);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  useEffect(() => {
    if (targetSrc === displayedSrc) {
      setIsPreviewLoading(false);
      return;
    }
    setIsPreviewLoading(true);
    // pre-cache the next image so the browser has it ready
    const pre = new Image();
    pre.src = targetSrc;

    const t = setTimeout(() => {
      setDisplayedSrc(targetSrc);
    }, 1500);
    return () => clearTimeout(t);
  }, [targetSrc, displayedSrc]);
  return (
    <section id="configurador" className="py-28 lg:py-40 bg-[#F7F7F7]" data-testid="section-configurador">
      <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <Eyebrow num="04">Configurador visual</Eyebrow>
            <h2 className="font-display text-5xl lg:text-6xl uppercase mt-6">
              Diseña tu módulo.
            </h2>
          </div>
          <p className="max-w-md text-[#444] leading-relaxed">
            Selecciona tamaño, distribución, material y extras. Recibirás un
            presupuesto orientativo en segundos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Preview */}
          <div className="md:col-span-7 relative border border-black/10 bg-white overflow-hidden">
            <div className="relative aspect-[5/4] md:aspect-auto md:h-[680px] bg-[#EAEAEA] overflow-hidden">
              {/* fallback image (real photo) — underlay */}
              <img
                src={fallbackSrc}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-60"
              />
              {/* AI preview — debounced swap */}
              <img
                key={displayedSrc}
                src={displayedSrc}
                alt={`Preview módulo ${size} · ${layout} · ${material}${hasToallero ? " con toallero" : ""}`}
                onLoad={() => setIsPreviewLoading(false)}
                className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-700 ease-out ${
                  isPreviewLoading ? "opacity-40 scale-[1.015]" : "opacity-100 scale-100"
                }`}
                data-testid="configurator-preview"
              />
              {/* Loading overlay — appears immediately on any change, hides 1.5s later */}
              {isPreviewLoading && (
                <div
                  className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
                  data-testid="configurator-loading"
                >
                  <div className="bg-[#0A0A0A] text-white px-6 py-3 flex items-center gap-3 shadow-2xl">
                    <span className="loader-dots" aria-hidden="true">
                      <span /><span /><span />
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.22em]">Generando preview</span>
                  </div>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute top-6 left-6 right-6 flex items-start justify-between text-white">
                <Eyebrow num={`MOD-${size}`}>
                  <span className="text-white/85">{MATERIALS.find((m) => m.id === material)?.label}</span>
                </Eyebrow>
                <div className="text-right">
                  <div className="overline text-white/70">Presupuesto orientativo</div>
                  <div
                    className="font-display text-3xl lg:text-4xl"
                    data-testid="configurator-price"
                  >
                    {estimatedPrice.toLocaleString("es-ES")} €
                  </div>
                </div>
              </div>
              <div className="absolute left-6 bottom-6 text-white">
                <div className="font-heading text-2xl leading-tight">
                  {SIZES.find((s) => s.id === size)?.label}
                </div>
                <div className="text-white/75 text-sm mt-1">
                  Distribución {LAYOUTS.find((l) => l.id === layout)?.label.toLowerCase()} · {accessories.length} extras
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="md:col-span-5 bg-white border border-black/10 p-8 lg:p-10 flex flex-col">
            <div className="space-y-10 flex-1">
              {/* Size */}
              <div>
                <div className="overline mb-4">Tamaño</div>
                <div className="grid grid-cols-3 gap-2">
                  {SIZES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSize(s.id)}
                      data-testid={`config-size-${s.id}`}
                      className={`px-3 py-3 text-sm transition-all border ${
                        size === s.id
                          ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                          : "bg-white text-[#0A0A0A] border-black/15 hover:border-[#0A0A0A]"
                      }`}
                    >
                      <div className="font-display text-xl">{s.id}</div>
                      <div className="text-[11px] uppercase tracking-wider mt-1 opacity-80">{s.label.split(" · ")[1]}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Layout */}
              <div>
                <div className="overline mb-4">Distribución</div>
                <div className="flex gap-2 flex-wrap">
                  {LAYOUTS.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setLayout(l.id)}
                      data-testid={`config-layout-${l.id}`}
                      className={`px-4 py-2.5 text-[13px] tracking-wide border transition-colors ${
                        layout === l.id
                          ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                          : "bg-white text-[#0A0A0A] border-black/15 hover:border-[#0A0A0A]"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Material */}
              <div>
                <div className="overline mb-4">Material principal</div>
                <div className="grid grid-cols-3 gap-3">
                  {MATERIALS.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setMaterial(m.id)}
                      data-testid={`config-material-${m.id}`}
                      className={`relative aspect-square overflow-hidden border-2 transition-all ${
                        material === m.id ? "border-[#0A0A0A]" : "border-transparent hover:border-black/30"
                      }`}
                    >
                      <img src={m.img} alt={m.label} className="absolute inset-0 w-full h-full object-cover" />
                      {material === m.id && (
                        <div className="absolute top-1 right-1 bg-[#0A0A0A] text-white w-6 h-6 flex items-center justify-center">
                          <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-white/95 text-[#0A0A0A] text-[10px] uppercase tracking-wider py-1 px-2 truncate">
                        {m.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Accessories */}
              <div>
                <div className="overline mb-4">Extras</div>
                <div className="flex flex-wrap gap-2">
                  {ACCESSORIES.map((a) => {
                    const active = accessories.includes(a.key);
                    const disabled = a.restrictedSizes?.includes(size);
                    const accBtnClass = (() => {
                      if (disabled) return "bg-[#EAEAEA] text-[#999] border-[#EAEAEA] cursor-not-allowed line-through";
                      if (active) return "bg-[#0A0A0A] text-white border-[#0A0A0A]";
                      return "bg-white text-[#0A0A0A] border-black/15 hover:border-[#0A0A0A]";
                    })();
                    const renderAccIcon = () => {
                      if (disabled) return <X className="w-3 h-3" strokeWidth={2.5} />;
                      if (active) return <Check className="w-3 h-3" strokeWidth={2.5} />;
                      return <Plus className="w-3 h-3" strokeWidth={2.5} />;
                    };
                    return (
                      <button
                        key={a.key}
                        onClick={() => !disabled && toggleAcc(a.key)}
                        disabled={disabled}
                        title={disabled ? a.reason : ""}
                        data-testid={`config-acc-${a.key.toLowerCase().replace(/\s/g, "-")}`}
                        className={`inline-flex items-center gap-2 px-3 py-2 text-[12px] tracking-wide border transition-colors ${accBtnClass}`}
                      >
                        {renderAccIcon()}
                        {a.key}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 text-[11px] text-[#888]">
                  {SIZES.find((s) => s.id === size)?.label.split(" · ")[0]} ·{" "}
                  {ACCESSORIES.filter((a) => a.restrictedSizes?.includes(size)).length > 0
                    ? `${ACCESSORIES.filter((a) => a.restrictedSizes?.includes(size)).map((a) => a.key).join(", ")} no disponibles en este tamaño.`
                    : "Todos los extras disponibles."}
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-black/10">
              <div className="flex items-baseline justify-between">
                <div className="overline">Estimado · IVA no incl.</div>
                <div className="font-display text-3xl" data-testid="config-final-price">
                  {estimatedPrice.toLocaleString("es-ES")} €
                </div>
              </div>
              <button
                onClick={sendToContact}
                data-testid="config-cta-presupuesto"
                className="mt-6 w-full inline-flex items-center justify-center gap-3 px-6 py-4 text-[13px] uppercase tracking-wider bg-[#0A0A0A] text-[#F7F7F7] hover:bg-[#333] transition-colors"
              >
                {saved ? "Guardado" : "Solicitar presupuesto detallado"}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Materiales catalog ---------- */
const Materiales = () => (
  <section className="py-28 lg:py-40 bg-white border-y border-black/10" data-testid="section-materiales">
    <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-6">
          <Eyebrow num="05">Materiales</Eyebrow>
          <h2 className="font-display text-5xl lg:text-6xl uppercase mt-6">
            Tacto, textura, <br /> tiempo.
          </h2>
        </div>
        <p className="lg:col-span-5 lg:col-start-8 text-[#333] leading-relaxed">
          Trabajamos solo con materiales arquitectónicos: piedra natural,
          maderas certificadas y revestimientos minerales. Cada acabado
          envejece con gracia, no se gasta.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {MATERIALS.map((m, i) => (
          <motion.div
            key={m.id}
            {...MOTION_FADE_UP_30}
            transition={staggerTransition(i, 0.6, 0.1)}
            viewport={VIEWPORT_ONCE}
            className="group"
            data-testid={`material-card-${m.id}`}
          >
            <div className="relative aspect-[4/5] overflow-hidden border border-black/10">
              <img src={m.img} alt={m.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="pt-5 flex items-start justify-between">
              <div>
                <div className="font-heading text-xl">{m.label}</div>
                <div className="text-sm text-[#666] mt-1">{m.sub}</div>
              </div>
              <span className="num-eyebrow text-[#0A0A0A]">0{i + 1}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Proyectos / Galería ---------- */
const Proyectos = () => (
  <section id="proyectos" className="py-28 lg:py-40 bg-[#F7F7F7]" data-testid="section-proyectos">
    <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-7">
          <Eyebrow num="06">Proyectos</Eyebrow>
          <h2 className="font-display text-5xl lg:text-6xl uppercase mt-6">
            Cada baño, <br /> una historia.
          </h2>
        </div>
        <div className="lg:col-span-5 lg:flex lg:items-end">
          <p className="text-[#333] leading-relaxed">
            Hoteles boutique, promociones residenciales, residencias de
            estudiantes y co-living. Una selección de instalaciones recientes.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {GALLERY.map((g, i) => {
          // Mix spans
          const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
          return (
            <motion.div
              key={g.src}
              {...MOTION_FADE_UP_30}
              transition={staggerTransition(i)}
              viewport={VIEWPORT_ONCE_60}
              className={`${spans[i]} gallery-card group relative overflow-hidden border border-black/10`}
              data-testid={`project-card-${i}`}
            >
              <Link to={`/proyectos/${g.slug}`} className="block">
                <div className="relative aspect-[16/11]">
                  <img
                    src={g.src}
                    alt={g.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 flex items-end justify-between text-white">
                    <div>
                      <div className="overline text-white/70">
                        {g.place} · {g.year}
                      </div>
                      <div className="font-heading text-2xl lg:text-3xl mt-2">{g.title}</div>
                    </div>
                    <div className="w-12 h-12 border border-white/40 flex items-center justify-center backdrop-blur-sm group-hover:bg-white group-hover:text-black transition-colors">
                      <ArrowUpRight className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- Proceso ---------- */
const Proceso = () => {
  const steps = [
    { k: "01", t: "Briefing & medición", b: "Visita técnica o lectura de planos. Definimos tipologías y geometría." },
    { k: "02", t: "Diseño 3D", b: "Renderizado fotorrealista y validación con el cliente y el estudio." },
    { k: "03", t: "Fabricación", b: "Producción en planta con control unitario, pruebas hidráulicas y de carga." },
    { k: "04", t: "Logística", b: "Plan de transporte, izaje y entrega just-in-time en obra." },
    { k: "05", t: "Instalación", b: "Conexión a acometidas, sellado y entrega del baño funcionando." },
  ];
  return (
    <section id="proceso" className="py-28 lg:py-40 bg-white border-y border-black/10" data-testid="section-proceso">
      <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-5">
            <Eyebrow num="07">Proceso</Eyebrow>
            <h2 className="font-display text-5xl lg:text-6xl uppercase mt-6">
              De la idea <br /> a la ducha caliente.
            </h2>
          </div>
          <p className="lg:col-span-5 lg:col-start-8 self-end text-[#333] leading-relaxed">
            Un protocolo claro, cinco fases. Tú decides el diseño. Nosotros
            nos ocupamos del resto.
          </p>
        </div>
        <ol className="divide-y divide-black/10 border-y border-black/10">
          {steps.map((s, i) => (
            <motion.li
              key={s.k}
              {...MOTION_FADE_UP_20}
              transition={staggerTransition(i, 0.5, 0.05)}
              viewport={VIEWPORT_ONCE}
              className="grid grid-cols-12 gap-6 py-8 group hover:bg-[#0A0A0A] hover:text-white transition-colors px-2"
              data-testid={`process-step-${s.k}`}
            >
              <div className="col-span-2 lg:col-span-1 font-display text-3xl lg:text-4xl">{s.k}</div>
              <div className="col-span-10 lg:col-span-4 font-heading text-2xl lg:text-3xl">{s.t}</div>
              <div className="col-span-12 lg:col-span-6 lg:col-start-7 text-[#444] group-hover:text-white/70 leading-relaxed">
                {s.b}
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ---------- Contacto (form) ---------- */
const EMPTY_FORM = { name: "", email: "", phone: "", project_type: "", bathroom_units: "", message: "" };

const formatConfigMessage = (cfg) =>
  `Configuración: ${cfg.config_size || ""} | ${cfg.config_layout || ""} | ${cfg.config_material || ""} | Extras: ${(cfg.config_accessories || []).join(", ")} | Estimado: ${cfg.estimated_price || ""}€`;

const readSavedConfig = () => {
  try {
    return JSON.parse(localStorage.getItem("modulaccion_config") || "null");
  } catch (err) {
    if (IS_DEV) console.warn("[modulaccion] could not parse saved config", err);
    return null;
  }
};

const buildQuotePayload = (form, cfg) => ({
  ...form,
  bathroom_units: form.bathroom_units ? Number(form.bathroom_units) : null,
  config_size: cfg?.config_size || null,
  config_layout: cfg?.config_layout || null,
  config_material: cfg?.config_material || null,
  config_accessories: cfg?.config_accessories || null,
});

const Contacto = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const applyConfig = (cfg) => {
    if (!cfg) return;
    setForm((f) => ({
      ...f,
      project_type: f.project_type || "Configurador online",
      message: formatConfigMessage(cfg),
    }));
  };

  useEffect(() => {
    // Initial read (in case user reloaded after saving a config)
    try {
      const raw = localStorage.getItem("modulaccion_config");
      if (raw) applyConfig(JSON.parse(raw));
    } catch (err) {
      if (IS_DEV) console.warn("[modulaccion] could not read saved config from localStorage", err);
    }

    // Live update when Configurador dispatches the event
    const handler = (e) => applyConfig(e.detail || null);
    window.addEventListener("modulaccion:config-saved", handler);
    return () => window.removeEventListener("modulaccion:config-saved", handler);
    // applyConfig is defined inside the component but only reads/writes form state via setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setStatus({ state: "error", msg: "Nombre y email son obligatorios." });
      return;
    }
    setStatus({ state: "loading", msg: "Enviando..." });
    try {
      await axios.post(`${API}/quotes`, buildQuotePayload(form, readSavedConfig()));
      setStatus({ state: "ok", msg: "Recibido. Te contactamos en menos de 24 h." });
      setForm(EMPTY_FORM);
    } catch (err) {
      setStatus({ state: "error", msg: "Error al enviar. Vuelve a intentarlo." });
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-black/30 focus:border-[#0A0A0A] py-3 text-base placeholder:text-[#999] outline-none transition-colors";

  return (
    <section id="contacto" className="py-28 lg:py-40 bg-[#F7F7F7]" data-testid="section-contacto">
      <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Eyebrow num="08">Presupuesto</Eyebrow>
            <h2 className="font-display text-5xl lg:text-6xl uppercase mt-6">
              Cuéntanos <br /> tu proyecto.
            </h2>
            <p className="mt-6 text-[#333] leading-relaxed max-w-md">
              Una respuesta personalizada en menos de 24 horas. Sin
              compromiso, sin formularios infinitos.
            </p>

            <div className="mt-12 space-y-5 text-sm">
              <div className="flex items-center gap-4">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                <a href="mailto:info@modulaccion.com" className="link-underline">info@modulaccion.com</a>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                <a href="tel:+34979699533" className="link-underline">+34 979 699 533</a>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="w-4 h-4 mt-1 shrink-0" strokeWidth={1.5} />
                <span>C/ Ferrocarril, 2 · Dueñas · 34210 Palencia · ES</span>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-white border border-black/10 p-8 lg:p-12"
            data-testid="quote-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <label className="overline">Nombre *</label>
                <input
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="Tu nombre"
                  data-testid="form-name"
                />
              </div>
              <div>
                <label className="overline">Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="email@empresa.com"
                  data-testid="form-email"
                />
              </div>
              <div>
                <label className="overline">Teléfono</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="+34"
                  data-testid="form-phone"
                />
              </div>
              <div>
                <label className="overline">Tipo de proyecto</label>
                <select
                  name="project_type"
                  value={form.project_type}
                  onChange={handleChange}
                  className={`${inputCls} appearance-none`}
                  data-testid="form-project-type"
                >
                  <option value="">Selecciona</option>
                  <option>Hotel</option>
                  <option>Residencial</option>
                  <option>Co-living</option>
                  <option>Residencia de estudiantes</option>
                  <option>Otro</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline">Nº de baños (aprox.)</label>
                <input
                  type="number"
                  min={1}
                  name="bathroom_units"
                  value={form.bathroom_units}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="ej. 24"
                  data-testid="form-units"
                />
              </div>
              <div className="md:col-span-2">
                <label className="overline">Mensaje</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="Cuéntanos plazos, ubicación y necesidades específicas..."
                  data-testid="form-message"
                />
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
              <div className="text-[12px] text-[#666] max-w-xs">
                Al enviar aceptas nuestra política de privacidad. No
                compartimos tus datos.
              </div>
              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid="form-submit"
                className="inline-flex items-center gap-3 px-7 py-4 text-[13px] uppercase tracking-wider bg-[#0A0A0A] text-[#F7F7F7] hover:bg-[#333] transition-colors disabled:opacity-50"
              >
                {status.state === "loading" ? "Enviando..." : "Enviar solicitud"}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            {status.state !== "idle" && status.state !== "loading" && (
              <div
                className={`mt-6 text-sm py-3 px-4 ${
                  status.state === "ok"
                    ? "bg-[#2D8A56]/10 text-[#2D8A56] border border-[#2D8A56]/30"
                    : "bg-[#D9402B]/10 text-[#D9402B] border border-[#D9402B]/30"
                }`}
                data-testid="form-status"
              >
                {status.msg}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQ = () => {
  const items = [
    {
      q: "¿Qué es un baño modular Modulacción?",
      a: "Es una unidad completa de baño —estructura, instalaciones, sanitarios y acabados— fabricada en planta y entregada lista para conectar a las acometidas de obra. Llega plug & play.",
    },
    {
      q: "¿Pierdo flexibilidad de diseño?",
      a: "No. Cada módulo se proyecta a medida con el estudio de arquitectura. No partimos de catálogo: partimos de tu plano.",
    },
    {
      q: "¿Qué obra se ahorra?",
      a: "Todo el solapamiento de oficios húmedos en obra (alicatado, fontanería, sanitario, vidriería...). Reducimos hasta un 72% el plazo de los baños.",
    },
    {
      q: "¿Para qué tipos de proyecto encaja?",
      a: "Hoteles, residencial multifamiliar, residencias de estudiantes, co-living, oficinas y proyectos institucionales. Donde repita tipología, gana sentido.",
    },
    {
      q: "¿Qué garantía ofrecéis?",
      a: "10 años en estructura e instalaciones, 2 años en acabados, garantía de marca en sanitarios y grifería.",
    },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section className="py-28 lg:py-40 bg-[#F7F7F7]" data-testid="section-faq">
      <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-5">
            <Eyebrow num="09">FAQ</Eyebrow>
            <h2 className="font-display text-5xl lg:text-6xl uppercase mt-6">
              Preguntas <br /> frecuentes.
            </h2>
          </div>
        </div>
        <div className="border-t border-black/15">
          {items.map((it, i) => {
            const isOpen = i === open;
            return (
              <div key={it.q} className="border-b border-black/15">
                <button
                  className="w-full py-6 lg:py-8 flex items-start justify-between gap-8 text-left"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-heading text-xl lg:text-2xl pr-4">{it.q}</span>
                  <span className="shrink-0 w-10 h-10 border border-[#0A0A0A] flex items-center justify-center">
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      {...MOTION_FAQ_PANEL}
                      className="overflow-hidden"
                    >
                      <div className="pb-8 text-[#444] leading-relaxed max-w-3xl">{it.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const h = (hash) => (isHome ? hash : `/${hash}`);
  return (
  <footer className="bg-[#0A0A0A] text-[#F7F7F7]" data-testid="site-footer">
    <div className="mx-auto max-w-[1480px] px-6 lg:px-10 pt-24 pb-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-20 border-b border-white/15">
        <div className="lg:col-span-7">
          <Eyebrow num="∞"><span className="text-white/60">Listos para empezar</span></Eyebrow>
          <h3 className="font-display text-5xl lg:text-7xl uppercase mt-6 leading-[0.9]">
            ¿Listo para <br /> dejar de construir <br />
            <span className="italic font-light">baños</span>?
          </h3>
          <div className="mt-10 inline-flex">
            <a
              href={h("#contacto")}
              className="group inline-flex items-center gap-3 px-7 py-4 text-[13px] uppercase tracking-wider bg-[#F7F7F7] text-[#0A0A0A] hover:bg-white transition-colors"
              data-testid="footer-cta"
            >
              Solicita presupuesto
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" strokeWidth={1.5} />
            </a>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="overline text-white/60 mb-5">Navegación</div>
          <ul className="space-y-3 text-sm">
            <li><a href={h("#concepto")} className="link-underline">Concepto</a></li>
            <li><a href={h("#beneficios")} className="link-underline">Beneficios</a></li>
            <li><a href={h("#configurador")} className="link-underline">Configurador</a></li>
            <li><a href={h("#proyectos")} className="link-underline">Proyectos</a></li>
          </ul>
        </div>
        <div className="lg:col-span-3">
          <div className="overline text-white/60 mb-5">Contacto</div>
          <ul className="space-y-3 text-sm">
            <li><a href="mailto:info@modulaccion.com" className="link-underline">info@modulaccion.com</a></li>
            <li><a href="tel:+34979699533" className="link-underline">+34 979 699 533</a></li>
            <li>C/ Ferrocarril, 2 · Dueñas<br/>34210 Palencia · ES</li>
          </ul>
          <div className="mt-8 flex gap-3">
            <a href="#" className="w-10 h-10 border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-colors" aria-label="Instagram"><Instagram className="w-4 h-4" strokeWidth={1.5} /></a>
            <a href="#" className="w-10 h-10 border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-colors" aria-label="LinkedIn"><Linkedin className="w-4 h-4" strokeWidth={1.5} /></a>
          </div>
        </div>
      </div>
      <div className="mt-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div className="font-display text-xl tracking-tighter">© 2026 MODULACCIÓN BAÑOS</div>
        <div className="flex items-center gap-6">
          <a
            href="/laboratorio.html"
            className="text-[12px] text-white/45 hover:text-white/80 tracking-wide transition-colors"
            data-testid="footer-laboratorio-link"
          >
            Laboratorio
          </a>
          <div className="text-[12px] text-white/60 tracking-wide uppercase">Industria + Arquitectura · Made in Spain</div>
        </div>
      </div>
    </div>
  </footer>
  );
};

/* ---------- Landing ---------- */
const Landing = () => (
  <>
    <Hero />
    <Ribbon />
    <Concepto />
    <Beneficios />
    <Configurador />
    <Materiales />
    <Proyectos />
    <Proceso />
    <Contacto />
    <FAQ />
  </>
);

/* ---------- Hash-aware scroll-to handler ---------- */
const ScrollToHash = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      // wait a tick so the target section is rendered
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 60);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);
  return null;
};

/* ---------- Main App ---------- */
function App() {
  useEffect(() => {
    // verify backend is up (non-blocking, fire-and-forget)
    axios.get(`${API}/`).catch((err) => {
      if (IS_DEV) console.warn("[modulaccion] backend health-check failed", err?.message);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <BrowserRouter>
      <ScrollToHash />
      <div className="App font-body" data-testid="app-root">
        <Header />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/proyectos/:slug" element={<ProjectDetail />} />
          <Route path="/laboratorio" element={<LaboratorioRedirect />} />
          <Route path="/app/laboratorio" element={<LaboratorioRedirect />} />
          <Route path="*" element={<Landing />} />
        </Routes>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
